import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Header } from '../../components/shared/header/header';
import { LeadsTable, LeadColumn, LeadRow, FilterConfig } from '../../components/shared/leads-table/leads-table';
import { DateTimePickerComponent } from '../../components/calendar-date-time/datetime-picker.component';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { finalize } from 'rxjs';
import { AdminCampaignService } from '../../services/admin-campaign.service';
import { AdminCampaignApiItem, CampaignCreateApiRequest, CampaignParticipantApiItem, CampaignStatusToggleApiItem, CampaignUpdateApiRequest } from '../../interfaces/admin-campaign.interface';
import { CampaignTypeApiItem } from '../../interfaces/campaign-type.interface';
import { runtimeLogger } from '../../utils/runtime-logger.util';

type CampaignParticipantSortField = 'codigo' | 'cpf' | 'nome' | 'telefone' | 'email';

interface CampaignParticipantRow {
  codigo: string;
  cpf: string;
  nome: string;
  telefone: string;
  telefoneRaw: string;
  email: string;
}

@Component({
  selector: 'app-admin-panel',
  standalone: true,
  imports: [CommonModule, FormsModule, Header, LeadsTable, DateTimePickerComponent, MatSnackBarModule],
  templateUrl: './admin-panel.html',
  styleUrls: ['./admin-panel.scss']
})
export class AdminPanel implements OnInit {
  tableColumns: LeadColumn[] = [
    { key: 'codigo', label: 'Código', type: 'text' },
    { key: 'nomeCampanha', label: 'Nome da Campanha', type: 'text', truncate: true },
    { key: 'tipoCampanha', label: 'Tipo da Campanha', type: 'text', truncate: true },
    { key: 'dataInicio', label: 'Data de Início', type: 'text' },
    { key: 'dataTermino', label: 'Data de término', type: 'text' },
    { key: 'statusCampanha', label: 'Status da campanha', type: 'switch' },
    { key: 'editarCampanha', label: 'Editar campanha', type: 'action', actionLabel: 'Editar' },
  ];

  readonly adminSortableColumns: string[] = ['codigo', 'nomeCampanha', 'tipoCampanha', 'dataInicio', 'dataTermino', 'statusCampanha'];
  readonly prioritizedTypeIds: number[] = [1, 2, 3];

  filterConfig: FilterConfig = {
    searchFields: ['nomeCampanha', 'codigo'],
    alphabeticalField: 'nomeCampanha',
    filterFields: {},
    dateField: 'dataInicio',
  };

  adminFilterOptions: { value: string, label: string }[] = [];
  campaignTypes: CampaignTypeApiItem[] = [];

  tableData: LeadRow[] = [];

  loading: boolean = false;
  emptyMessage: string = 'Nenhuma campanha encontrada';
  errorMessage: string = '';

  tooltipMouseX: number = 0;
  tooltipMouseY: number = 0;
  showDisabledEditTooltip: boolean = false;
  disabledTooltipMessage: string = 'Não é possível alterar campanhas deste tipo.';
  showTextTooltip: boolean = false;
  textTooltipContent: string = '';

  // Variável para controlar a exibição do modal
  showNovaCampanhaModal: boolean = false;
  showEditarCampanhaModal: boolean = false;
  showCampaignParticipantsModal: boolean = false;
  isSavingNewCampaign: boolean = false;
  isSavingEditCampaign: boolean = false;
  campaignParticipantsLoading: boolean = false;
  campaignParticipantsErrorMessage: string = '';
  selectedParticipantsCampaignCode: string = '';
  selectedParticipantsCampaignTypeName: string = '';
  campaignParticipants: CampaignParticipantRow[] = [];
  campaignParticipantsCurrentPage: number = 0;
  readonly campaignParticipantsPageSize: number = 5;
  readonly campaignParticipantsColumns: { key: CampaignParticipantSortField; label: string }[] = [
    { key: 'codigo', label: 'Código' },
    { key: 'cpf', label: 'CPF' },
    { key: 'nome', label: 'Nome' },
    { key: 'telefone', label: 'Telefone' },
    { key: 'email', label: 'E-mail' }
  ];
  campaignParticipantsSortState: { field: CampaignParticipantSortField; order: 'asc' | 'desc' } = {
    field: 'nome',
    order: 'asc'
  };

  newCampaignForm: {
    cdCampanha: string;
    idTipoCampanha: number | null;
    nmCampanha: string;
    flStatus: '' | 'A' | 'I';
    arquivoLeads: File | null;
  } = {
    cdCampanha: '',
    idTipoCampanha: null,
    nmCampanha: '',
    flStatus: '',
    arquivoLeads: null
  };

  selectedCampaignId?: number;
  editCampaignForm: {
    cdCampanha: string;
    idTipoCampanha: number | null;
    nmCampanha: string;
    flStatus: '' | 'A' | 'I';
  } = {
    cdCampanha: '',
    idTipoCampanha: null,
    nmCampanha: '',
    flStatus: ''
  };

  // Variáveis para armazenar as datas
  dataInicio?: Date;
  dataTermino?: Date;

  private currentDataIniFilter: string = '';
  private currentDataFimFilter: string = '';
  private currentTipoCampanhaFilter?: number;
  private readonly blockedCampaignTypeIds: number[] = [1, 2, 3];
  private readonly nonSelectableCampaignTypeIds: number[] = [1, 2, 3];

  constructor(
    private adminCampaignService: AdminCampaignService,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.currentDataIniFilter = this.getThreeMonthsAgoDate();
    this.currentDataFimFilter = this.getTodayDate();
    this.loadCampaignTypes();
    this.loadCampaigns(this.currentDataIniFilter, this.currentDataFimFilter);
  }

  private generateAdminFilterOptions(): void {
    this.adminFilterOptions = this.campaignTypes.map((campaignType) => ({
        value: campaignType.idTipoCampanha.toString(),
        label: campaignType.nmTipoCampanha
      }));
  }

  private loadCampaignTypes(): void {
    this.adminCampaignService.getCampaignTypes().subscribe({
      next: (campaignTypes: CampaignTypeApiItem[]) => {
        this.campaignTypes = [...campaignTypes]
          .sort((campaignTypeA, campaignTypeB) => campaignTypeA.idTipoCampanha - campaignTypeB.idTipoCampanha);
        this.generateAdminFilterOptions();
      },
      error: (error) => {
        console.error('Erro ao carregar tipos de campanha:', error);
        this.campaignTypes = [];
        this.adminFilterOptions = [];
      }
    });
  }

  get editableCampaignTypes(): CampaignTypeApiItem[] {
    return this.campaignTypes.filter((campaignType) =>
      !this.nonSelectableCampaignTypeIds.includes(campaignType.idTipoCampanha)
    );
  }

  get sortedCampaignParticipants(): CampaignParticipantRow[] {
    const collator = new Intl.Collator('pt-BR', { numeric: true, sensitivity: 'base' });

    return [...this.campaignParticipants].sort((participantA, participantB) => {
      const valueA = this.getCampaignParticipantSortValue(participantA, this.campaignParticipantsSortState.field);
      const valueB = this.getCampaignParticipantSortValue(participantB, this.campaignParticipantsSortState.field);
      const comparison = collator.compare(valueA, valueB);

      return this.campaignParticipantsSortState.order === 'asc' ? comparison : -comparison;
    });
  }

  get paginatedCampaignParticipants(): CampaignParticipantRow[] {
    const startIndex = this.campaignParticipantsCurrentPage * this.campaignParticipantsPageSize;
    const endIndex = startIndex + this.campaignParticipantsPageSize;
    return this.sortedCampaignParticipants.slice(startIndex, endIndex);
  }

  private loadCampaigns(dataIni?: string, dataFim?: string, idTipoCampanha?: number): void {
    this.loading = true;
    this.errorMessage = '';

    this.adminCampaignService.getCampaigns(dataIni, dataFim, idTipoCampanha).subscribe({
      next: (campaigns: AdminCampaignApiItem[]) => {
        this.tableData = campaigns.map((campaign) => {
          const isBlockedCampaignType = this.isCampaignTypeBlocked(campaign.idTipoCampanha);

          return {
            idCampanha: campaign.idCampanha,
            idLead: campaign.idCampanha,
            codigo: campaign.cdCampanha,
            nomeCampanha: campaign.dsCampanha,
            tipoCampanha: campaign.nmTipoCampanha,
            idTipoCampanha: campaign.idTipoCampanha,
            dtIniRaw: campaign.dtIni,
            dtFimRaw: campaign.dtFim,
            dataInicio: this.formatDate(campaign.dtIni),
            dataTermino: this.formatDate(campaign.dtFim),
            flStatus: (campaign.flStatus || (campaign.ativo ? 'A' : 'I')).toUpperCase(),
            statusCampanha: campaign.flStatus === 'A' || campaign.ativo,
            statusCampanhaDisabled: isBlockedCampaignType,
            editarCampanha: 'editar',
            editarCampanhaDisabled: !campaign.editavel,
            consultarLeads: 'Consultar'
          };
        });
      },
      error: (error) => {
        console.error('Erro ao carregar campanhas:', error);
        this.tableData = [];
        this.errorMessage = 'Erro ao carregar campanhas';
        this.loading = false;
      },
      complete: () => {
        this.loading = false;
      }
    });
  }

  private getTodayDate(): string {
    const today = new Date();
    return today.toISOString().split('T')[0];
  }

  private getThreeMonthsAgoDate(): string {
    const today = new Date();
    const threeMonthsAgo = new Date(today);
    threeMonthsAgo.setMonth(today.getMonth() - 3);
    return threeMonthsAgo.toISOString().split('T')[0];
  }

  private formatDate(value: string): string {
    if (!value) {
      return '-';
    }

    const rawDate = value.includes('T') ? value.split('T')[0] : value;

    if (rawDate.includes('-')) {
      const [year, month, day] = rawDate.split('-');
      if (year && month && day) {
        return `${day}/${month}/${year}`;
      }
    }

    return rawDate;
  }

  private isCampaignTypeBlocked(idTipoCampanha: number): boolean {
    return this.blockedCampaignTypeIds.includes(idTipoCampanha);
  }

  onAdminTooltipMouseMove(event: MouseEvent): void {
    this.tooltipMouseX = event.clientX;
    this.tooltipMouseY = event.clientY;

    const target = event.target as HTMLElement | null;
    const actionContainer = target?.closest('.action-container');
    const switchContainer = target?.closest('.switch-container');
    const hasDisabledActionButton = !!actionContainer?.querySelector('.action-button.disabled');
    const hasDisabledSwitch = !!switchContainer?.querySelector('.custom-switch.disabled');

    this.showDisabledEditTooltip = hasDisabledActionButton || hasDisabledSwitch;
    if (this.showDisabledEditTooltip) {
      this.disabledTooltipMessage = 'Não é possível alterar campanhas deste tipo.';
    }

    const truncatedCell = target?.closest('.truncated-cell') as HTMLElement | null;
    if (truncatedCell) {
      this.textTooltipContent = truncatedCell.textContent?.trim() || '';
      this.showTextTooltip = true;
    } else {
      this.showTextTooltip = false;
      this.textTooltipContent = '';
    }
  }

  onAdminTooltipMouseLeave(): void {
    this.showDisabledEditTooltip = false;
    this.showTextTooltip = false;
    this.textTooltipContent = '';
  }

  onBlockedCodeMouseMove(event: MouseEvent): void {
    this.tooltipMouseX = event.clientX;
    this.tooltipMouseY = event.clientY;
    this.disabledTooltipMessage = 'Não é possível alterar o código da campanha.';
    this.showDisabledEditTooltip = true;
  }

  onBlockedCodeMouseLeave(): void {
    this.showDisabledEditTooltip = false;
  }

  onDataInicioChange(date: Date): void {
    this.dataInicio = date;

    if (this.dataTermino && this.dataTermino < date) {
      this.dataTermino = undefined;
    }

    runtimeLogger.debug('Data de início selecionada:', date);
  }

  onDataTerminoChange(date: Date): void {
    runtimeLogger.debug('Data de término selecionada:', date);

    this.dataTermino = date;
  }

  // Método para abrir o modal
  openNovaCampanhaModal(): void {
    this.resetNewCampaignForm();
    this.resetDatas();
    this.showNovaCampanhaModal = true;
  }

  // Método para fechar o modal
  closeNovaCampanhaModal(): void {
    this.showNovaCampanhaModal = false;
    this.isSavingNewCampaign = false;
    this.resetNewCampaignForm();
    this.resetDatas();
  }

  onArquivoLeadsChange(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const selectedFile = inputElement.files?.[0] ?? null;
    this.newCampaignForm.arquivoLeads = selectedFile;
  }

  baixarModeloArquivo(event: Event): void {
    event.preventDefault();

    this.adminCampaignService.downloadCampaignTemplate().subscribe({
      next: (response) => {
        const fileBlob = response.body;
        if (!fileBlob) {
          alert('Não foi possível baixar o modelo do arquivo.');
          return;
        }

        const fileName = this.extractDownloadFileName(response.headers.get('content-disposition'))
          || 'MODELO_CADASTRO_CAMPANHA.xlsx';
        const downloadUrl = window.URL.createObjectURL(fileBlob);
        const link = document.createElement('a');

        link.href = downloadUrl;
        link.download = fileName;
        link.style.display = 'none';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
      },
      error: (error) => {
        console.error('Erro ao baixar modelo de cadastro de campanha:', error);
        alert(this.extractApiErrorMessage(error, 'Erro ao baixar modelo do arquivo. Tente novamente.'));
      }
    });
  }

  // Método para abrir o modal
  openEditarCampanhaModal(campaignRow: LeadRow): void {
    const selectedCampaignId = Number(campaignRow['idCampanha'] ?? campaignRow['idLead']);
    if (!selectedCampaignId) {
      return;
    }

    const dataInicio = this.parseDateValue(campaignRow['dtIniRaw'] ?? campaignRow['dataInicio']);
    const dataTermino = this.parseDateValue(campaignRow['dtFimRaw'] ?? campaignRow['dataTermino']);

    this.selectedCampaignId = selectedCampaignId;
    this.editCampaignForm = {
      cdCampanha: String(campaignRow['codigo'] ?? ''),
      idTipoCampanha: Number(campaignRow['idTipoCampanha']) || null,
      nmCampanha: String(campaignRow['nomeCampanha'] ?? ''),
      flStatus: this.normalizeStatusFlag(campaignRow['flStatus'], campaignRow['statusCampanha'])
    };

    this.dataInicio = dataInicio;
    this.dataTermino = dataTermino;
    this.showEditarCampanhaModal = true;
  }

  // Método para fechar o modal
  closeEditarCampanhaModal(): void {
    this.showEditarCampanhaModal = false;
    this.selectedCampaignId = undefined;
    this.resetEditCampaignForm();
    this.resetDatas();
  }

  openCampaignParticipantsModal(campaignRow: LeadRow): void {
    const campaignCode = String(campaignRow['codigo'] ?? '').trim();
    if (!campaignCode) {
      alert('Campanha selecionada inválida.');
      return;
    }

    this.showCampaignParticipantsModal = true;
    this.selectedParticipantsCampaignCode = campaignCode;
    this.selectedParticipantsCampaignTypeName = String(campaignRow['tipoCampanha'] ?? '').trim();
    this.campaignParticipants = [];
    this.campaignParticipantsErrorMessage = '';
    this.campaignParticipantsCurrentPage = 0;
    this.campaignParticipantsSortState = {
      field: 'nome',
      order: 'asc'
    };
    this.campaignParticipantsLoading = true;

    this.adminCampaignService.getCampaignParticipants(campaignCode)
      .pipe(finalize(() => {
        this.campaignParticipantsLoading = false;
      }))
      .subscribe({
        next: (participants: CampaignParticipantApiItem[]) => {
          this.campaignParticipants = participants.map((participant) => this.mapCampaignParticipant(participant, campaignCode));
        },
        error: (error) => {
          console.error('Erro ao carregar participantes da campanha:', error);
          this.campaignParticipantsErrorMessage = this.extractApiErrorMessage(
            error,
            'Erro ao carregar participantes da campanha. Tente novamente.'
          );
        }
      });
  }

  closeCampaignParticipantsModal(): void {
    this.showCampaignParticipantsModal = false;
    this.campaignParticipantsLoading = false;
    this.campaignParticipantsErrorMessage = '';
    this.selectedParticipantsCampaignCode = '';
    this.selectedParticipantsCampaignTypeName = '';
    this.campaignParticipants = [];
    this.campaignParticipantsCurrentPage = 0;
    this.campaignParticipantsSortState = {
      field: 'nome',
      order: 'asc'
    };
  }

  // Método para resetar as datas
  resetDatas(): void {
    this.dataInicio = undefined;
    this.dataTermino = undefined;
  }

  private resetEditCampaignForm(): void {
    this.editCampaignForm = {
      cdCampanha: '',
      idTipoCampanha: null,
      nmCampanha: '',
      flStatus: ''
    };
  }

  private resetNewCampaignForm(): void {
    this.newCampaignForm = {
      cdCampanha: '',
      idTipoCampanha: null,
      nmCampanha: '',
      flStatus: '',
      arquivoLeads: null
    };
  }

  private normalizeStatusFlag(statusFlag: unknown, statusBoolean?: unknown): 'A' | 'I' {
    const normalizedStatus = String(statusFlag ?? '').trim().toUpperCase();
    if (normalizedStatus === 'A' || normalizedStatus === 'I') {
      return normalizedStatus;
    }

    return statusBoolean ? 'A' : 'I';
  }

  private parseDateValue(value: unknown): Date | undefined {
    if (!value) {
      return undefined;
    }

    const stringValue = String(value).trim();
    if (!stringValue || stringValue === '-') {
      return undefined;
    }

    if (stringValue.includes('T') || /^\d{4}-\d{2}-\d{2}$/.test(stringValue)) {
      const datePart = stringValue.split('T')[0];
      const [year, month, day] = datePart.split('-').map(Number);
      if (!year || !month || !day) {
        return undefined;
      }
      return new Date(year, month - 1, day);
    }

    if (/^\d{2}\/\d{2}\/\d{4}$/.test(stringValue)) {
      const [day, month, year] = stringValue.split('/').map(Number);
      if (!year || !month || !day) {
        return undefined;
      }
      return new Date(year, month - 1, day);
    }

    return undefined;
  }

  // Método para fechar o modal ao clicar no overlay
  closeModalOnOverlayClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('modal-overlay')) {
      this.closeNovaCampanhaModal();
      this.closeEditarCampanhaModal();
      this.closeCampaignParticipantsModal();
    }
  }

  toggleCampaignParticipantsSort(field: CampaignParticipantSortField): void {
    if (this.campaignParticipantsSortState.field === field) {
      this.campaignParticipantsSortState.order = this.campaignParticipantsSortState.order === 'asc' ? 'desc' : 'asc';
    } else {
      this.campaignParticipantsSortState = {
        field,
        order: 'asc'
      };
    }

    this.campaignParticipantsCurrentPage = 0;
  }

  getCampaignParticipantsSortOrder(field: CampaignParticipantSortField): 'asc' | 'desc' | null {
    return this.campaignParticipantsSortState.field === field
      ? this.campaignParticipantsSortState.order
      : null;
  }

  previousCampaignParticipantsPage(): void {
    if (this.hasPreviousCampaignParticipantsPage()) {
      this.campaignParticipantsCurrentPage -= 1;
    }
  }

  nextCampaignParticipantsPage(): void {
    if (this.hasNextCampaignParticipantsPage()) {
      this.campaignParticipantsCurrentPage += 1;
    }
  }

  hasPreviousCampaignParticipantsPage(): boolean {
    return this.campaignParticipantsCurrentPage > 0;
  }

  hasNextCampaignParticipantsPage(): boolean {
    return (this.campaignParticipantsCurrentPage + 1) * this.campaignParticipantsPageSize < this.campaignParticipants.length;
  }

  getCampaignParticipantsDisplayedRange(): string {
    const totalItems = this.campaignParticipants.length;

    if (totalItems === 0) {
      return '0 de 0';
    }

    const startIndex = this.campaignParticipantsCurrentPage * this.campaignParticipantsPageSize + 1;
    const endIndex = Math.min(
      startIndex + this.paginatedCampaignParticipants.length - 1,
      totalItems
    );

    return `${startIndex}-${endIndex} de ${totalItems}`;
  }

  // Método para salvar a campanha
  salvarCampanha(): void {
    if (this.isSavingNewCampaign) {
      return;
    }

    const validationErrors = this.validateNewCampaignForm();
    if (validationErrors.length > 0) {
      alert(validationErrors.join('\n'));
      return;
    }

    if (!this.dataInicio || !this.dataTermino) {
      alert('Data de início e data de fim são obrigatórias.');
      return;
    }

    if (this.dataTermino < this.dataInicio) {
      alert('A data de fim deve ser maior ou igual à data de início.');
      return;
    }

    const payload: CampaignCreateApiRequest = {
      cdCampanha: this.newCampaignForm.cdCampanha.trim(),
      idTipoCampanha: Number(this.newCampaignForm.idTipoCampanha),
      nmCampanha: this.newCampaignForm.nmCampanha.trim(),
      dtIni: this.toIsoDate(this.dataInicio),
      dtFim: this.toIsoDate(this.dataTermino),
      flStatus: this.newCampaignForm.flStatus as 'A' | 'I',
      arquivoLeads: this.newCampaignForm.arquivoLeads as File
    };

    this.isSavingNewCampaign = true;

    this.adminCampaignService.createCampaign(payload)
      .pipe(finalize(() => {
        this.isSavingNewCampaign = false;
      }))
      .subscribe({
      next: () => {
        this.closeNovaCampanhaModal();
        this.loadCampaigns(this.currentDataIniFilter, this.currentDataFimFilter, this.currentTipoCampanhaFilter);
        this.snackBar.open('Campanha criada com sucesso.', 'Fechar', {
          duration: 4000,
          horizontalPosition: 'right',
          verticalPosition: 'top'
        });
      },
      error: (error) => {
        console.error('Erro ao cadastrar campanha:', error);
        alert(this.extractApiErrorMessage(error, 'Erro ao cadastrar campanha. Tente novamente.'));
      }
    });
  }

  salvarEdicaoCampanha(): void {
    if (this.isSavingEditCampaign) {
      return;
    }

    if (!this.selectedCampaignId) {
      alert('Campanha selecionada inválida.');
      return;
    }

    const validationErrors = this.validateEditCampaignForm();
    if (validationErrors.length > 0) {
      alert(validationErrors.join('\n'));
      return;
    }

    if (!this.dataInicio || !this.dataTermino) {
      alert('Data de início e data de fim são obrigatórias.');
      return;
    }

    if (this.dataTermino < this.dataInicio) {
      alert('A data de fim deve ser maior ou igual à data de início.');
      return;
    }

    const payload: CampaignUpdateApiRequest = {
      idCampanha: this.selectedCampaignId,
      idTipoCampanha: Number(this.editCampaignForm.idTipoCampanha),
      nmCampanha: this.editCampaignForm.nmCampanha.trim(),
      dtIni: this.toIsoDate(this.dataInicio),
      dtFim: this.toIsoDate(this.dataTermino),
      flStatus: this.editCampaignForm.flStatus as 'A' | 'I'
    };

    this.isSavingEditCampaign = true;

    this.adminCampaignService.updateCampaign(payload)
      .pipe(finalize(() => {
        this.isSavingEditCampaign = false;
      }))
      .subscribe({
      next: () => {
        this.closeEditarCampanhaModal();
        this.loadCampaigns(this.currentDataIniFilter, this.currentDataFimFilter, this.currentTipoCampanhaFilter);
        this.snackBar.open('Campanha alterada com sucesso.', 'Fechar', {
          duration: 4000,
          horizontalPosition: 'right',
          verticalPosition: 'top'
        });
      },
      error: (error) => {
        console.error('Erro ao atualizar campanha:', error);
        alert(this.extractApiErrorMessage(error, 'Erro ao atualizar campanha. Tente novamente.'));
      }
    });
  }

  private validateEditCampaignForm(): string[] {
    const errors: string[] = [];

    if (!this.editCampaignForm.idTipoCampanha) {
      errors.push('Tipo da campanha é obrigatório.');
    }

    if (!this.editCampaignForm.nmCampanha.trim()) {
      errors.push('Nome da campanha é obrigatório.');
    }

    if (!this.dataInicio) {
      errors.push('Data de início é obrigatória.');
    }

    if (!this.dataTermino) {
      errors.push('Data de fim é obrigatória.');
    }

    if (this.editCampaignForm.flStatus !== 'A' && this.editCampaignForm.flStatus !== 'I') {
      errors.push('Status da campanha é obrigatório.');
    }

    return errors;
  }

  private validateNewCampaignForm(): string[] {
    const errors: string[] = [];

    if (!this.newCampaignForm.cdCampanha.trim()) {
      errors.push('Código da campanha é obrigatório.');
    }

    if (!this.newCampaignForm.idTipoCampanha) {
      errors.push('Tipo da campanha é obrigatório.');
    }

    if (!this.newCampaignForm.nmCampanha.trim()) {
      errors.push('Nome da campanha é obrigatório.');
    }

    if (!this.dataInicio) {
      errors.push('Data de início é obrigatória.');
    }

    if (!this.dataTermino) {
      errors.push('Data de fim é obrigatória.');
    }

    if (this.newCampaignForm.flStatus !== 'A' && this.newCampaignForm.flStatus !== 'I') {
      errors.push('Status da campanha é obrigatório.');
    }

    if (!this.newCampaignForm.arquivoLeads) {
      errors.push('Arquivo de leads é obrigatório.');
    } else if (!this.newCampaignForm.arquivoLeads.name.toLowerCase().endsWith('.xlsx')) {
      errors.push('O arquivo de leads deve estar no formato .xlsx.');
    }

    return errors;
  }

  private extractApiErrorMessage(error: any, fallbackMessage: string): string {
    const apiErrors = error?.error?.errors;
    if (Array.isArray(apiErrors) && apiErrors.length > 0) {
      return apiErrors.join('\n');
    }

    if (typeof error?.error === 'string' && error.error.trim()) {
      return error.error;
    }

    if (typeof error?.error?.message === 'string' && error.error.message.trim()) {
      return error.error.message;
    }

    return fallbackMessage;
  }

  private extractDownloadFileName(contentDisposition: string | null): string | null {
    if (!contentDisposition) {
      return null;
    }

    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
      return decodeURIComponent(utf8Match[1].trim());
    }

    const asciiMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
    return asciiMatch?.[1]?.trim() || null;
  }

  private toIsoDate(value: Date): string {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Mantendo os métodos existentes...
  onAdminSearchChange(searchValue: string): void {
    runtimeLogger.debug('Campanha selecionada:', searchValue);

    if (!searchValue) {
      this.currentTipoCampanhaFilter = undefined;
      runtimeLogger.debug('Mostrar todos os tipos de campanha');
      this.loadCampaigns(this.currentDataIniFilter, this.currentDataFimFilter);
    } else {
      this.currentTipoCampanhaFilter = Number(searchValue);
      runtimeLogger.debug('Filtrar por tipo de campanha:', searchValue);
      this.loadCampaigns(this.currentDataIniFilter, this.currentDataFimFilter, this.currentTipoCampanhaFilter);
    }
  }

  onSwitchChange(event: { row: LeadRow; field: string; value: boolean }): void {
    runtimeLogger.debug('Switch alterado:', event);

    const campaignId = Number(event.row['idLead']);
    const disabledKey = `${event.field}Disabled`;
    const fallbackPreviousStatus: 'A' | 'I' = event.value ? 'I' : 'A';
    const previousStatus = String(event.row['flStatus'] ?? '').trim().toUpperCase();
    const previousStatusFlag: 'A' | 'I' = previousStatus === 'A' || previousStatus === 'I'
      ? previousStatus
      : fallbackPreviousStatus;

    if (event.row[disabledKey]) {
      return;
    }

    if (!campaignId) {
      event.row[event.field] = !event.value;
      return;
    }

    event.row[disabledKey] = true;

    this.adminCampaignService.toggleCampaignStatus(campaignId).subscribe({
      next: (response: CampaignStatusToggleApiItem) => {
        event.row[event.field] = response.ativo;
        event.row['statusCampanha'] = response.ativo;

        const updatedStatus = String(response.flStatus ?? '').trim().toUpperCase();
        event.row['flStatus'] = updatedStatus === 'A' || updatedStatus === 'I'
          ? updatedStatus
          : (response.ativo ? 'A' : 'I');
      },
      error: (error) => {
        console.error('Erro ao alterar status da campanha:', error);
        event.row[event.field] = !event.value;
        event.row['statusCampanha'] = !event.value;
        event.row['flStatus'] = previousStatusFlag;
        event.row[disabledKey] = false;
      },
      complete: () => {
        event.row[disabledKey] = false;
      }
    });
  }

  onActionClick(event: { row: LeadRow; action: string }): void {
    runtimeLogger.debug('Ação clicada:', event);

    if (event.action === 'editarCampanha' || event.action === 'editar') {
      runtimeLogger.debug('Editar campanha:', event.row);
      this.openEditarCampanhaModal(event.row);
    }
  }

  onPageChange(event: any): void {
    runtimeLogger.debug('Página alterada:', event);
  }

  onPageSizeChange(event: any): void {
    runtimeLogger.debug('Tamanho da página alterado:', event);
  }

  onViewLead(lead: LeadRow): void {
    runtimeLogger.debug('Visualizar campanha:', lead);

    this.openCampaignParticipantsModal(lead);
  }

  onSearchChange(searchText: string): void {
    runtimeLogger.debug('Busca alterada:', searchText);
  }

  onFilterChange(filters: string[]): void {
    runtimeLogger.debug('Filtros alterados:', filters);
  }

  onDateRangeChange(dateRange: {start: string, end: string}): void {
    runtimeLogger.debug('Intervalo de datas:', dateRange);

    if (!dateRange.start || !dateRange.end) {
      return;
    }

    if (
      dateRange.start === this.currentDataIniFilter
      && dateRange.end === this.currentDataFimFilter
    ) {
      return;
    }

    this.currentDataIniFilter = dateRange.start;
    this.currentDataFimFilter = dateRange.end;
    this.loadCampaigns(dateRange.start, dateRange.end, this.currentTipoCampanhaFilter);
  }

  onClearAllFilters(): void {
    runtimeLogger.debug('Limpar todos os filtros');

    this.currentDataIniFilter = '';
    this.currentDataFimFilter = '';
    this.currentTipoCampanhaFilter = undefined;
    this.loadCampaigns();
  }

  onSortChange(sort: {field: string, order: 'ASC' | 'DESC'}): void {
    runtimeLogger.debug('Ordenação:', sort);
  }

  private mapCampaignParticipant(
    participant: CampaignParticipantApiItem,
    fallbackCampaignCode: string
  ): CampaignParticipantRow {
    return {
      codigo: (participant.cdCampanha || fallbackCampaignCode).trim(),
      cpf: (participant.cdCpfCnpj || '').trim() || '-',
      nome: (participant.nmNome || '').trim() || '-',
      telefone: this.formatParticipantPhone(participant.nrDddCel, participant.nrTelefoneCel),
      telefoneRaw: this.buildParticipantPhoneSortValue(participant.nrDddCel, participant.nrTelefoneCel),
      email: (() => { const e = (participant.nmEmail || '').trim(); return (!e || e === 'nao.informado@vwfs.com') ? '-' : e; })()
    };
  }

  private formatParticipantPhone(areaCode: number | null, phoneNumber: number | null): string {
    const normalizedAreaCode = areaCode !== null && areaCode !== undefined
      ? String(areaCode).replace(/\D/g, '').padStart(2, '0')
      : '';
    const normalizedPhoneNumber = phoneNumber !== null && phoneNumber !== undefined
      ? String(phoneNumber).replace(/\D/g, '')
      : '';

    if (!normalizedAreaCode && !normalizedPhoneNumber) {
      return '-';
    }

    if (!normalizedPhoneNumber) {
      return normalizedAreaCode ? `(${normalizedAreaCode})` : '-';
    }

    if (normalizedPhoneNumber.length === 9) {
      return normalizedAreaCode
        ? `(${normalizedAreaCode}) ${normalizedPhoneNumber.slice(0, 5)}-${normalizedPhoneNumber.slice(5)}`
        : `${normalizedPhoneNumber.slice(0, 5)}-${normalizedPhoneNumber.slice(5)}`;
    }

    if (normalizedPhoneNumber.length === 8) {
      return normalizedAreaCode
        ? `(${normalizedAreaCode}) ${normalizedPhoneNumber.slice(0, 4)}-${normalizedPhoneNumber.slice(4)}`
        : `${normalizedPhoneNumber.slice(0, 4)}-${normalizedPhoneNumber.slice(4)}`;
    }

    return normalizedAreaCode
      ? `(${normalizedAreaCode}) ${normalizedPhoneNumber}`
      : normalizedPhoneNumber;
  }

  private buildParticipantPhoneSortValue(areaCode: number | null, phoneNumber: number | null): string {
    const normalizedAreaCode = areaCode !== null && areaCode !== undefined
      ? String(areaCode).replace(/\D/g, '').padStart(2, '0')
      : '';
    const normalizedPhoneNumber = phoneNumber !== null && phoneNumber !== undefined
      ? String(phoneNumber).replace(/\D/g, '')
      : '';

    return `${normalizedAreaCode}${normalizedPhoneNumber}`;
  }

  private getCampaignParticipantSortValue(
    participant: CampaignParticipantRow,
    field: CampaignParticipantSortField
  ): string {
    switch (field) {
      case 'codigo':
        return participant.codigo;
      case 'cpf':
        return participant.cpf;
      case 'nome':
        return participant.nome;
      case 'telefone':
        return participant.telefoneRaw;
      case 'email':
        return participant.email;
      default:
        return '';
    }
  }
}
